import React, { useState } from 'react';
import { useUserDataContext } from '../../contexts/UserDataContext';
import { cancelOrder } from '../../services/websocketApi/orders';
import './Orders.css';
import './OrdersResponsive.css'; // Import responsive CSS

/**
 * Orders component with CSS Grid layout
 */
const Orders = () => {
  const { userData, isSubscribed, isLoading, error } = useUserDataContext();
  const [cancellingOrderId, setCancellingOrderId] = useState(null);
  
  // --- Formatting Functions ---
  const formatNumber = (num, decimals = 2, minDecimals = 2) => {
    if (num === undefined || num === null) return '--';
    const parsedNum = parseFloat(num);
    if (isNaN(parsedNum)) return '--';
    return parsedNum.toLocaleString(undefined, {
      minimumFractionDigits: minDecimals,
      maximumFractionDigits: decimals
    });
  };
  
  const formatTime = (timestamp) => {
    if (!timestamp) return '--';
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString(undefined, {
        year: '2-digit', month: 'numeric', day: 'numeric', 
        hour: '2-digit', minute:'2-digit'
      });
    } catch (e) { return 'Invalid Date'; }
  };
  
  const getOrderTypeLabel = (type) => {
    const typeMap = {
      'market': 'Market',
      'limit': 'Limit',
      'stop': 'Stop',
      'stop_limit': 'Stop Limit',
      'trailing_stop': 'Trailing'
    };
    return typeMap[type] || type;
  };
  
  const formatSymbol = (symbol) => {
    if (!symbol) return '--';
    const parts = symbol.split('.');
    return parts.length > 1 ? parts[parts.length - 1] : symbol;
  };

  const getOrderStatusLabel = (status) => {
    const statusStyles = {
      'pending': 'status-pending',
      'open': 'status-open',
      'partially_filled': 'status-partially-filled',
      'filled': 'status-filled',
      'cancelled': 'status-cancelled',
      'rejected': 'status-rejected'
    };
    const statusLabels = {
      'pending': 'Pending',
      'open': 'Open',
      'partially_filled': 'Partial',
      'filled': 'Filled',
      'cancelled': 'Canceled',
      'rejected': 'Rejected'
    };
    const cssClass = statusStyles[status] || '';
    const label = statusLabels[status] || status;
    return <span className={`status-label ${cssClass}`}>{label}</span>;
  };
  
  // --- Event Handlers ---
  const handleCancelOrder = async (orderId) => {
    if (!orderId || cancellingOrderId) return; // Prevent double clicks
    
    setCancellingOrderId(orderId);
    try {
      await cancelOrder(orderId);
      // Order removal is handled by WebSocket update
    } catch (err) {
      console.error(`Failed to cancel order ${orderId}:`, err);
    } finally {
      setCancellingOrderId(null);
    }
  };
  
  // --- Render Empty/Error States ---
  const renderMessage = (type, message, icon = null) => (
    <div className="orders-container">
      <div className={`orders-message ${type === 'error' ? 'orders-message-error' : ''}`}>
        {icon && <div className="orders-message-icon">{icon}</div>}
        {message}
      </div>
    </div>
  );
  
  const renderEmptyState = () => (
    <div className="orders-container">
      <div className="empty-state">
        <div className="empty-state-icon">📋</div>
        <div className="empty-state-text">
          You don't have any active orders. Place an order to start trading.
        </div>
      </div>
    </div>
  );

  // --- Handle different states ---
  if (isLoading) {
    return renderMessage('loading', 'Loading orders...', '⏳');
  }
  
  // Error state
  if (error) {
    return renderMessage('error', `Error loading orders: ${error}`, '⚠️');
  }
  
  // Not subscribed state
  if (!isSubscribed) {
    return renderMessage('info', 'Not connected to order feed', '🔌');
  }
  
  // No positions state
  if (!userData || !userData.orders || userData.orders.length === 0) {
    return renderEmptyState();
  }

  return (
    <div className="orders-container">
      {/* Grid Header */}
      <div className="orders-header">
        <div className="orders-header-cell col-symbol">Symbol</div>
        <div className="orders-header-cell col-type">Type</div>
        <div className="orders-header-cell col-side">Side</div>
        <div className="orders-header-cell col-size">Size</div>
        <div className="orders-header-cell col-price">Price</div>
        <div className="orders-header-cell col-status">Status</div>
        <div className="orders-header-cell col-created">Created</div>
        <div className="orders-header-cell col-actions">Actions</div>
      </div>
      
      {/* Grid Body */}
      <div className="orders-body">
        {userData.orders.map((order) => {
          const isCancellable = order.status === 'open' || order.status === 'pending' || order.status === 'partially_filled';
          const sizeDecimals = Math.abs(order.quantity) >= 1 ? 0 : (Math.abs(order.quantity) >= 0.01 ? 2 : 4);
          const priceDecimals = order.price >= 10 ? 2 : (order.price >= 0.1 ? 4 : 5);

          return (
            <div className="orders-row" key={order.orderId || `${order.symbol}-${order.timestamp}`}>
              <div className="orders-cell col-symbol" title={order.symbol}>
                {formatSymbol(order.symbol)}
              </div>
              <div className="orders-cell col-type">
                {getOrderTypeLabel(order.orderType)}
              </div>
              <div className="orders-cell col-side">
                <span className={`side-badge ${order.side === 'buy' ? 'buy' : 'sell'}`}>
                  {order.side === 'buy' ? 'Buy' : 'Sell'}
                </span>
              </div>
              <div className="orders-cell col-size" title={order.quantity}>
                {formatNumber(order.quantity, sizeDecimals, 0)}
              </div>
              <div className="orders-cell col-price" title={order.price}>
                {formatNumber(order.price, priceDecimals)}
              </div>
              <div className="orders-cell col-status">
                {getOrderStatusLabel(order.status)}
              </div>
              <div className="orders-cell col-created" title={new Date(order.timestamp).toLocaleString()}>
                {formatTime(order.timestamp)}
              </div>
              <div className="orders-cell col-actions">
                {isCancellable && (
                  <button 
                    className="cancel-button"
                    onClick={() => handleCancelOrder(order.orderId)}
                    disabled={cancellingOrderId === order.orderId}
                    title="Cancel Order"
                  >
                    {cancellingOrderId === order.orderId ? '...' : 'Cancel'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Orders; 